import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CommonTable from "../../../../common/table/ComonTable";
import DeleteModal from "../../../../common/commonmodal/DeleteModal";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faUserGroup } from "@fortawesome/free-solid-svg-icons";
import apiClient from "../../../../../services/apiClient";

const VendorList = () => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    
    const roleName = (user?.RoleName || user?.roleName || user?.role || user?.Role || localStorage.getItem('roleName') || "").toLowerCase();
    const isSiteAdmin = roleName.includes('site admin') || roleName.includes('super');
    const assignedSiteId = user?.siteId || 2;

    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0
    });

    const [deleteModal, setDeleteModal] = useState({ isOpen: false, vendorId: null, vendorName: "" });

    const fetchVendors = useCallback(async (customParams = {}) => {
        setLoading(true);
        try {
            const params = {
                siteId: assignedSiteId,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
                ...customParams
            };

            const cleanParams = Object.fromEntries(
                Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== "")
            );

            const response = await apiClient.get('/vendor-management/vendor/GetVendors', {
                params: cleanParams
            });

            if (response.data?.isSuccess && response.data?.data) {
                const list = response.data.data.map(v => ({
                    ...v,
                    id: v.vendorId
                }));
                setVendors(list);
                setPagination(prev => ({
                    ...prev,
                    totalRecords: response.data.totalCount || list.length
                }));
            } else {
                setVendors([]);
            }
        } catch (error) {
            console.error("Failed to fetch vendors:", error);
            toast.error("Failed to load vendors");
            setVendors([]);
        } finally {
            setLoading(false);
        }
    }, [assignedSiteId, pagination.pageNumber, pagination.pageSize]);

    useEffect(() => {
        fetchVendors();
    }, [fetchVendors]);

    const tableHeaders = [
        { label: "Vendor Name", key: "vendorName" },
        { label: "Category", key: "categoryName" },
        { label: "Contact Person", key: "contactPerson" },
        { label: "Contact Mobile", key: "mobileNumber" },
        { label: "Email Address", key: "email" },
        { label: "Status", key: "statusLabel" },
        { label: "Action", key: "action" }
    ];

    const renderTableCell = (key, row) => {
        if (key === "vendorName") {
            return (
                <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-800 dark:text-textsecondary">{row.vendorName || "—"}</span>
                    {row.isApprovalRequired === false && (
                        <span className="text-[10px] text-orange-600 font-semibold bg-orange-50 dark:bg-orange-900/30 dark:text-orange-400 px-2 py-0.5 rounded-full w-fit mt-1 border border-orange-200 dark:border-orange-800/50" title="This vendor bypasses unit-level staff approval requirements.">
                            Approval Bypassed
                        </span>
                    )}
                </div>
            );
        }
        if (key === "statusLabel") {
            const isActive = (row.status || "").toLowerCase() === "active";
            return (
                <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${isActive ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"}`}>
                    {row.status || "Active"}
                </span>
            );
        }
        if (key === "categoryName") {
            return (
                <div className="flex flex-col">
                    <span className="text-sm font-semibold text-gray-800 dark:text-textprimary">{row.categoryName || "—"}</span>
                    {row.subCategories && Array.isArray(row.subCategories) && row.subCategories.length > 0 && (
                        <span className="text-[11px] text-gray-400 truncate max-w-[180px]" title={row.subCategories.join(', ')}>
                            {row.subCategories.join(', ')}
                        </span>
                    )}
                </div>
            );
        }
        return <span className="text-sm font-medium text-gray-800 dark:text-textsecondary">{row[key] || "—"}</span>;
    };

    const onPageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, pageNumber: newPage }));
    };

    const handleLimitChange = (newLimit) => {
        setPagination((prev) => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
    };

    const handleSuspendVendor = async () => {
        if (!deleteModal.vendorId) return;
        try {
            await apiClient.put(`/vendor-management/vendor/SuspendVendor?vendorId=${deleteModal.vendorId}`, {
                reason: "Suspended by Admin",
                status: "Suspended"
            });
            toast.success(`Vendor ${deleteModal.vendorName} suspended successfully`);
            setDeleteModal({ isOpen: false, vendorId: null, vendorName: "" });
            fetchVendors();
        } catch (error) {
            console.error("Failed to suspend vendor:", error);
            toast.error("Failed to suspend vendor");
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs items={["Workforce Management", "Vendor", "List"]} />

            <PageHeadingSection
                Navbar={true}
                mainHeading="Vendor Management"
                subHeading="Manage vendors, their staff, and permissions across the site and units."
                primaryBtnText="Onboard Vendor"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => navigate("/zentcomm/workforce-management/vendor/onboard")}
            />

            <div className="shrink-0 flex-1 flex flex-col mt-6">
                <CommonTable 
                    loading={loading}
                    tableHeading="Vendor List"
                    headers={tableHeaders}
                    data={vendors}
                    renderCell={renderTableCell}
                    pagination={{ 
                        pageNumber: pagination.pageNumber, 
                        pageSize: pagination.pageSize, 
                        totalRecords: pagination.totalRecords,
                        totalPages: Math.ceil(pagination.totalRecords / pagination.pageSize) || 1 
                    }}
                    onPageChange={onPageChange}
                    onLimitChange={handleLimitChange}
                    isLoading={loading}
                    onEdit={(row) => navigate(`/zentcomm/workforce-management/vendor/onboard?vendorId=${row.vendorId || row.id}`, { state: { vendorData: row, vendorId: row.vendorId || row.id } })}
                    onDelete={(row) => setDeleteModal({ isOpen: true, vendorId: row.vendorId || row.id, vendorName: row.vendorName })}
                    customAction={{
                        label: "View Staff",
                        icon: faUserGroup,
                        onClick: (row) => navigate(`/zentcomm/workforce-management/vendor/staff?vendorId=${row.vendorId || row.id}&vendorName=${encodeURIComponent(row.vendorName || '')}`)
                    }}
                />
            </div>

            <DeleteModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, vendorId: null, vendorName: "" })}
                onConfirm={handleSuspendVendor}
                title="Suspend Vendor"
                message={`Are you sure you want to suspend "${deleteModal.vendorName}"?`}
            />
        </div>
    );
};

export default VendorList;
